import InactiveDeleteOptionEditToolbar from "features/EditToolbar";
import {
  CheckboxGroupInput,
  FunctionField,
  Labeled,
  ReferenceField,
  SimpleForm,
  TextField,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { ClassTypes, CLASSES, StudentServicePath } from "utils/constants";
import { useLocation, useParams } from "react-router";
import { studentRequests } from "api/studentApi";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { Box, Chip, Grid, Typography } from "@mui/material";
import { administrationRequests } from "api/administrationApi";

import { useFormContext, useWatch } from "react-hook-form";
import { FormControlLabel, Checkbox } from "@mui/material";
import { useNotifyError } from "utils/useNotifyError";
import AttendanceQrCodeDialog from "./AttendanceQrCodeDialog";

const SelectAllButton = ({ students, source }) => {
  const { setValue } = useFormContext();
  const selectedItems = useWatch({ name: source });

  const isAllSelected = students.length > 0 && selectedItems.length === students.length;

  const handleChange = event => {
    if (!event.target.checked) {
      setValue(source, [], { shouldDirty: true, shouldValidate: true });
    } else {
      const allIds = students.map(s => s.studentEducationId);
      setValue(source, allIds, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={isAllSelected}
          indeterminate={selectedItems.length > 0 && selectedItems.length < students.length}
          onChange={handleChange}
          color="primary"
        />
      }
      label={
        <>
          {isAllSelected ? "Отмаркирай всички" : "Маркирай всички"}{" "}
          <Box
            component="span"
            sx={{ color: "primary.main", fontWeight: "bold" }}
          >
            ({selectedItems.length}/{students.length})
          </Box>
        </>
      }
    />
  );
};

const StudentCheckboxForm = () => {
  const record = useRecordContext();
  const params = useParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const notifyError = useNotifyError();

  const students = Array.isArray(record.students) ? record.students : [];
  const canUpdate = checkPermission(getPermissions(), permissions.canUpdateClassAttendance);

  const location = useLocation();
  const studyGroups = location.state?.studyGroups || [];
  const disciplineName = location.state?.disciplineName;
  const classTypeId = location.state?.classTypeId;
  const specialitiesNames = location.state?.specialitiesNames || [];
  const date = location.state?.date;
  const startTime = location.state?.startTime;
  const endTime = location.state?.endTime;
  const returnTo = location.state?.returnTo ?? `/${StudentServicePath}${CLASSES}`;

  const sortedGroups = [...studyGroups].sort((a, b) => a - b).join(", ");
  const isLectureClass = (classTypeId ?? record?.classTypeId) === ClassTypes.Lectures;

  const defaultChecked = students.filter(s => s.attended).map(s => s.studentEducationId);

  const onSubmit = async values => {
    const checkedIds = values?.attendants?.studentEducationId ?? [];

    const body = {
      classId: Number(params.id),
      attendants: students.map(s => ({
        studentEducationId: s.studentEducationId,
        attended: checkedIds.includes(s.studentEducationId),
      })),
    };

    try {
      await dataProvider.customRequest(studentRequests.updateClassAttendance(params.id), body, "PUT");
      notify("Присъствията са записани.", { type: "success" });
      redirect(returnTo);
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <>
      <Grid
        container
        sx={{ px: 3, pt: 3 }}
      >
        <Grid
          item
          xs={12}
          sx={{ mb: 2 }}
        >
          <Labeled label="Специалности">
            <FunctionField
              label="Специалности"
              render={() => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {specialitiesNames?.map((spec, index) => (
                    <Chip
                      key={index}
                      label={spec}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            />
          </Labeled>
        </Grid>
        {disciplineName && (
          <Grid
            item
            xs={12}
            md={2}
          >
            <Labeled label="Дисциплина">
              <FunctionField render={() => <Typography>{disciplineName}</Typography>} />
            </Labeled>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={2}
        >
          <Labeled label="Дата">
            <FunctionField render={() => <Typography>{date}</Typography>} />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
        >
          <Labeled label="Час">
            <FunctionField
              render={() => (
                <Typography>
                  {startTime} - {endTime}
                </Typography>
              )}
            />
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
        >
          <Labeled label="Тип занятие">
            <ReferenceField
              source="id"
              reference={administrationRequests.getRoomClassTypesSelect()}
              record={{ id: classTypeId }}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
        >
          <Labeled label="Учебни групи">
            <FunctionField render={() => <Typography>{sortedGroups || "Всички групи"}</Typography>} />
          </Labeled>
        </Grid>
        {record?.topicName && (
          <Grid
            item
            xs={12}
            md={2}
          >
            <Labeled label="Тема">
              <TextField source="topicName" />
            </Labeled>
          </Grid>
        )}

        {canUpdate && isLectureClass && (
          <AttendanceQrCodeDialog
            classId={Number(params.id)}
            onStop={refresh}
          />
        )}
      </Grid>
      <SimpleForm
        // Remount the form so the checkboxes reflect the students who marked themselves through the QR code
        key={defaultChecked.join(",")}
        toolbar={<InactiveDeleteOptionEditToolbar alwaysEnable />}
        onSubmit={onSubmit}
        defaultValues={{ attendants: { studentEducationId: defaultChecked } }}
      >
        {students.length === 0 ? (
          "Няма намерени студенти."
        ) : (
          <>
            {canUpdate && (
              <SelectAllButton
                students={students}
                source="attendants.studentEducationId"
              />
            )}
            <CheckboxGroupInput
              label="Присъствие на студенти"
              source="attendants.studentEducationId"
              choices={students}
              readOnly={!canUpdate}
              optionText={rec =>
                rec.secondName
                  ? `${rec.facultyNumber} - ${rec.firstName} ${rec.secondName} ${rec.lastName}`
                  : `${rec.facultyNumber} - ${rec.firstName} ${rec.lastName}`
              }
              optionValue="studentEducationId"
              defaultValue={defaultChecked}
              sx={{ "& .RaCheckboxGroupInput-label": { fontSize: 22, color: "primary.main", mb: 1, px: 2 } }}
              row={false}
            />
          </>
        )}
      </SimpleForm>
    </>
  );
};

export default StudentCheckboxForm;
